/**
 * Vendor details on the business page (requirements p.1; gap list §3; review PAGES-3).
 * "Vendor" is a capability of the organization: add it to an existing business (no duplicate organization), edit it,
 * or retire it (kept, marked inactive). Vendor files are the organization's files in the "Vendor" category.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Card from "../ui/Card";
import Button from "../common/Button";
import Input from "../common/Input";
import { useToast } from "../ui/Toast";
import { useConfirmDialog } from "../ui/ConfirmDialog";
import { formatPhoneNumber, formatDateMDY } from "../../utils/formatters";
import {
  deactivateVendorProfile,
  describeVendorError,
  getVendorProfile,
  saveVendorProfile,
  validateVendorFields,
} from "../../services/organizationVendor.service";
import type { VendorFields } from "../../services/organizationVendor.service";
import {
  getOrganizations,
  getOrganization,
} from "../../services/organizations.service";

interface Props {
  organizationId: string;
  organizationName: string;
  organizationEmail?: string | null;
  organizationPhone?: string | null;
  vendorOnly: boolean;
  canEdit: boolean;
  startEditing?: boolean;
  vendorFileCount?: number;
  onOpenFiles?: () => void;
}

const AssociatedOrgPicker: React.FC<{
  value: string | null;
  excludeId: string;
  onChange: (id: string | null) => void;
}> = ({ value, excludeId, onChange }) => {
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const { data: current } = useQuery({
    queryKey: ["organization", value],
    queryFn: () => getOrganization(value as string),
    enabled: !!value,
  });
  const { data: results } = useQuery({
    queryKey: ["associated-org-search", submitted],
    queryFn: () =>
      getOrganizations({
        page: 1,
        limit: 8,
        search: submitted,
        sortBy: "name",
        sortOrder: "asc",
      }),
    enabled: submitted.trim().length >= 2,
  });
  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1">
        Associated business (optional)
      </span>
      {value ? (
        <div className="flex items-center gap-2 text-sm">
          <span>{current?.name ?? "Selected business"}</span>
          <button
            type="button"
            className="text-red-600 underline"
            onClick={() => onChange(null)}
          >
            clear
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              aria-label="Search associated business"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={q}
              placeholder="Search by business name"
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setSubmitted(q.trim());
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setSubmitted(q.trim())}
            >
              Search
            </Button>
          </div>
          {results && (
            <ul className="max-h-40 overflow-y-auto rounded border divide-y text-sm">
              {results.data.filter((o) => o.id !== excludeId).length === 0 && (
                <li className="p-2 text-gray-500">No match.</li>
              )}
              {results.data
                .filter((o) => o.id !== excludeId)
                .map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      className="w-full p-2 text-left hover:bg-gray-50"
                      onClick={() => onChange(o.id)}
                    >
                      {o.name}
                      <span className="text-gray-500">
                        {" "}
                        {[o.address_city, o.address_state]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const VendorProfileCard: React.FC<Props> = ({
  organizationId,
  organizationName,
  organizationEmail,
  organizationPhone,
  vendorOnly,
  canEdit,
  startEditing,
  vendorFileCount,
  onOpenFiles,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const { data: profile, isLoading } = useQuery({
    queryKey: ["vendor-profile", organizationId],
    queryFn: () => getVendorProfile(organizationId),
  });
  const { data: associated } = useQuery({
    queryKey: ["organization", profile?.associated_organization_id],
    queryFn: () =>
      getOrganization(profile!.associated_organization_id as string),
    enabled: !!profile?.associated_organization_id,
  });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<VendorFields>({
    vendor_name: "",
    vendor_number: "",
    vendor_email: "",
    vendor_phone: "",
    associated_organization_id: null,
    notes: "",
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof VendorFields, string>>
  >({});
  const [serverError, setServerError] = useState<string | null>(null);

  const openEditor = () => {
    setForm(
      profile
        ? {
            vendor_name: profile.vendor_name,
            vendor_number: profile.vendor_number,
            vendor_email: profile.vendor_email,
            vendor_phone: profile.vendor_phone,
            associated_organization_id: profile.associated_organization_id,
            notes: profile.notes ?? "",
          }
        : {
            vendor_name: organizationName,
            vendor_number: "",
            vendor_email: organizationEmail ?? "",
            vendor_phone: organizationPhone ?? "",
            associated_organization_id: null,
            notes: "",
          },
    );
    setErrors({});
    setServerError(null);
    setEditing(true);
  };

  useEffect(() => {
    if (startEditing && !isLoading && canEdit && !profile?.is_active) {
      openEditor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startEditing, isLoading]);

  const save = useMutation({
    mutationFn: () => saveVendorProfile(organizationId, form, !!profile),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["vendor-profile", organizationId],
      });
      queryClient.invalidateQueries({ queryKey: ["vendor-index"] });
      setEditing(false);
      showToast("success", "Vendor details saved");
    },
    onError: (e: Error) => setServerError(describeVendorError(e.message)),
  });
  const retire = useMutation({
    mutationFn: () => deactivateVendorProfile(organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["vendor-profile", organizationId],
      });
      queryClient.invalidateQueries({ queryKey: ["vendor-index"] });
      showToast("success", "Vendor details retired");
    },
    onError: (e: Error) => showToast("error", describeVendorError(e.message)),
  });

  const submit = () => {
    const e = validateVendorFields(form);
    setErrors(e);
    if (Object.keys(e).length === 0) {
      save.mutate();
    }
  };

  const active = profile?.is_active === true;
  if (isLoading) {
    return null;
  }
  if (!profile && !canEdit) {
    return null;
  }

  return (
    <Card>
      <div
        className="flex flex-wrap items-center justify-between gap-2 mb-4"
        data-testid="section-vendor"
      >
        <h3 className="text-lg font-semibold text-gray-900">
          Vendor{" "}
          {active && (
            <span className="ml-2 rounded bg-purple-100 px-2 py-0.5 text-sm font-medium text-purple-800">
              {vendorOnly ? "Vendor only" : "Business + Vendor"}
            </span>
          )}
          {profile && !active && (
            <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-sm text-gray-600">
              Retired
            </span>
          )}
        </h3>
        {canEdit && !editing && (
          <div className="flex gap-2">
            {active ? (
              <>
                <Button size="sm" variant="secondary" onClick={openEditor}>
                  Edit vendor details
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: "Retire vendor details?",
                        message:
                          "This organization will no longer be listed as a vendor. The details are kept and can be re-activated.",
                        confirmText: "Retire",
                        variant: "warning",
                      })
                    ) {
                      retire.mutate();
                    }
                  }}
                >
                  Retire
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={openEditor}
                data-testid="add-vendor-details"
              >
                {profile ? "Re-activate vendor details" : "Add vendor details"}
              </Button>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-4" data-testid="vendor-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Vendor Name"
              required
              value={form.vendor_name}
              error={errors.vendor_name}
              onChange={(e) =>
                setForm({ ...form, vendor_name: e.target.value })
              }
            />
            <Input
              label="Vendor Number"
              required
              value={form.vendor_number}
              error={errors.vendor_number}
              onChange={(e) =>
                setForm({ ...form, vendor_number: e.target.value })
              }
            />
            <Input
              label="Vendor Email"
              type="email"
              required
              value={form.vendor_email}
              error={errors.vendor_email}
              onChange={(e) =>
                setForm({ ...form, vendor_email: e.target.value })
              }
            />
            <Input
              label="Vendor Phone"
              type="tel"
              required
              value={form.vendor_phone}
              error={errors.vendor_phone}
              onChange={(e) =>
                setForm({ ...form, vendor_phone: e.target.value })
              }
            />
          </div>
          <AssociatedOrgPicker
            value={form.associated_organization_id ?? null}
            excludeId={organizationId}
            onChange={(id) =>
              setForm({ ...form, associated_organization_id: id })
            }
          />
          <div>
            <label
              htmlFor="vendor-notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes
            </label>
            <textarea
              id="vendor-notes"
              rows={2}
              maxLength={2000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={form.notes ?? ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          {serverError && (
            <div
              role="alert"
              className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            >
              {serverError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button onClick={submit} isLoading={save.isPending}>
              Save vendor details
            </Button>
          </div>
        </div>
      ) : profile ? (
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <dt className="text-sm text-gray-600">Vendor Name</dt>
            <dd className="text-gray-900" data-testid="vendor-name">
              {profile.vendor_name}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-600">Vendor Number</dt>
            <dd className="font-mono text-gray-900" data-testid="vendor-number">
              {profile.vendor_number}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-600">Vendor Email</dt>
            <dd className="text-gray-900 break-all">{profile.vendor_email}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-600">Vendor Phone</dt>
            <dd className="text-gray-900">
              {formatPhoneNumber(profile.vendor_phone)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-600">Associated business</dt>
            <dd className="text-gray-900">
              {profile.associated_organization_id ? (
                <Link
                  className="text-blue-600 hover:underline"
                  to={`/organizations/${profile.associated_organization_id}`}
                >
                  {associated?.name ?? "Open"}
                </Link>
              ) : (
                <span className="text-gray-400">Not set</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-gray-600">Vendor files</dt>
            <dd className="text-gray-900">
              {vendorFileCount ?? 0}{" "}
              {onOpenFiles && (
                <button
                  className="ml-1 text-blue-600 hover:underline"
                  onClick={onOpenFiles}
                >
                  Open Documents
                </button>
              )}
            </dd>
          </div>
          {profile.notes && (
            <div className="md:col-span-2">
              <dt className="text-sm text-gray-600">Notes</dt>
              <dd className="whitespace-pre-line text-gray-900">
                {profile.notes}
              </dd>
            </div>
          )}
          {!active && profile.deactivated_at && (
            <div className="md:col-span-2 text-sm text-gray-500">
              Retired {formatDateMDY(profile.deactivated_at)}
            </div>
          )}
        </dl>
      ) : (
        <p className="text-sm text-gray-500">
          This organization is not a vendor. Use “Add vendor details” if it also
          supplies us; no separate organization is needed.
        </p>
      )}
      {vendorOnly && active && !editing && (
        <p className="mt-4 text-xs text-gray-500">
          Vendor-only records have no facility type or physical address, so they
          never appear on maps, the voucher locator or redemption lists.
        </p>
      )}
      <ConfirmDialogComponent />
    </Card>
  );
};

export default VendorProfileCard;
